import React, { useState, useEffect } from 'react';
import apiClient from '../utils/apiClient';
import { getSelectedSport } from '../utils/helpers';
import PlayByPlay from './PlayByPlay';


const GameStatSlider = ({ gameId, teamId, onClose, isOpen }) => {

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [gameStats, setGameStats] = useState(null);
  const [playByPlay, setPlayByPlay] = useState(null);
  const [playByPlayLoading, setPlayByPlayLoading] = useState(false);
  const [playByPlayError, setPlayByPlayError] = useState(null);
  const [activeTab, setActiveTab] = useState('stats');
  const [teamStats, setTeamStats] = useState({
    currentTeamStats: null,
    opponentTeamStats: null,
    gameInfo: {
      date: '',
      homeTeam: '',
      awayTeam: '',
      homeScore: 0,
      awayScore: 0,
      isHome: false,
      isWin: false
    }
  });
  const [sportType, setSportType] = useState('');

  useEffect(() => {
    if (isOpen && gameId) {
      const currentSport = getSelectedSport();
      setSportType(currentSport);
      fetchGameStats();
    }
  }, [gameId, isOpen]);

  const fetchGameStats = async () => {
    setLoading(true);
    setError(null);

    try {
      let response = await apiClient.get(`${getSelectedSport()}/games/${gameId}/team-stats?schema=${getSelectedSport()}`);
      console.log('response==>', response);
      response = response?.data;

      if (response && Array.isArray(response)) {
        setGameStats({ items: response });
        processGameStats(response, teamId);
      } else {
        setError('Invalid response format from API');
      }
    } catch (error) {
      console.error('Error fetching game stats:', error);
      setError(`Failed to load game statistics: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchPlayByPlay = async () => {
    if (playByPlay) return;

    setPlayByPlayLoading(true);
    setPlayByPlayError(null);

    try {
      let response = await apiClient.get(`${getSelectedSport()}/play-by-play/game/${gameId}?schema=${getSelectedSport()}`);
      console.log('Play-by-play response==>', response);

      if (response?.data?.success && response?.data?.data) {
        setPlayByPlay(response.data.data);
      } else {
        setPlayByPlayError('Invalid play-by-play response format from API');
      }
    } catch (error) {
      console.error('Error fetching play-by-play:', error);
      setPlayByPlayError(`Failed to load play-by-play data: ${error.message}`);
    } finally {
      setPlayByPlayLoading(false);
    }
  };

  const processGameStats = (statsItems, currentTeamId) => {
    if (!statsItems || statsItems.length === 0 || !currentTeamId) {
      setError('No stats available for this game');
      return;
    }

    const currentSport = getSelectedSport();

    if (currentSport === 'WBB' || currentSport === 'MBB') {
      processBasketballStats(statsItems, currentTeamId);
    } else if (currentSport === 'CBB') {
      processBaseballStats(statsItems, currentTeamId);
    } else {
      setError(`Unsupported sport type: ${currentSport}`);
    }
  };

  const processBasketballStats = (statsItems, currentTeamId) => {
    const teamStats = {};
    let homeTeam = null;
    let awayTeam = null;

    statsItems.forEach(stat => {
      if (stat.Location === 'H') {
        homeTeam = { id: stat.TeamID, name: stat.Team };
      } else if (stat.Location === 'V') {
        awayTeam = { id: stat.TeamID, name: stat.Team };
      }
      else if (stat.Location === 'N' && stat.PlayerType === "B") {
        // For neutral venues, we need to determine which team is which
        // You might need additional logic here based on your data structure
        if (!homeTeam) {
          homeTeam = { id: stat.TeamID, name: stat.Team };
        } else if (!awayTeam) {
          awayTeam = { id: stat.TeamID, name: stat.Team };
        }
      }

      teamStats[stat.TeamID] = {
        name: stat.Team,
        isCurrentTeam: String(stat.TeamID) === String(currentTeamId),
        opponent: stat.Opponent,
        opponentId: stat.OpponentID,
        location: stat.Location === 'H' ? 'Home' : stat.Location === 'V' ? 'Away' : 'Neutral',
        isWin: stat.WinOrLoss === 'W',
        gameDay: stat.GameDay,
        stats: stat
      };
    });

    const currentTeamData = Object.values(teamStats).find(team => team.isCurrentTeam);
    const opponentTeamData = Object.values(teamStats).find(team => !team.isCurrentTeam);

    if (!currentTeamData) {
      setError('Could not identify current team in game data');
      return;
    }

    const gameInfo = {
      date: currentTeamData.gameDay,
      homeTeam: homeTeam ? homeTeam.name : '',
      homeTeamId: homeTeam ? homeTeam.id : '',
      awayTeam: awayTeam ? awayTeam.name : '',
      awayTeamId: awayTeam ? awayTeam.id : '',
      homeScore: homeTeam && teamStats[homeTeam.id] ? teamStats[homeTeam.id].stats.PTS || 0 : 0,
      awayScore: awayTeam && teamStats[awayTeam.id] ? teamStats[awayTeam.id].stats.PTS || 0 : 0,
      isHome: currentTeamData.location === 'Home',
      isWin: currentTeamData.isWin,
      isNeutral: currentTeamData.location === 'Neutral' // Add this flag
    };

    setTeamStats({
      currentTeamStats: currentTeamData.stats,
      opponentTeamStats: opponentTeamData?.stats,
      gameInfo
    });
  };

  const processBaseballStats = (statsItems, currentTeamId) => {
    const teamStats = {};
    let homeTeam = {};
    let awayTeam = {};

    statsItems.forEach(stat => {
      if (!teamStats[stat.TeamID]) {
        teamStats[stat.TeamID] = {
          name: stat.Team,
          isCurrentTeam: String(stat.TeamID) === String(currentTeamId),
          opponent: stat.Opponent,
          opponentId: stat.OpponentID,
          location: stat.Location === 'H' ? 'Home' : stat.Location === 'V' ? 'Away' : 'Neutral',
          isWin: stat.WinOrLoss === 'W',
          gameDay: stat.GameDay,
          battingStats: null,
          pitchingStats: null
        };
      }

      if (stat.PlayerType === 'B') {
        teamStats[stat.TeamID].battingStats = stat;
      } else if (stat.PlayerType === 'P') {
        teamStats[stat.TeamID].pitchingStats = stat;
      }
      if (stat.Location === 'H') {
        homeTeam = { id: stat.TeamID, name: stat.Team };
      } else if (stat.Location === 'V') {
        awayTeam = { id: stat.TeamID, name: stat.Team };
      } else if (stat.Location === 'N') {
        // For neutral venues, determine team assignment
        if (stat.TeamID == currentTeamId) {
          homeTeam = { id: stat.TeamID, name: stat.Team };
        } else if (stat.TeamID != currentTeamId) {
          awayTeam = { id: stat.TeamID, name: stat.Team };
        }
      }

      // ... rest of the batting/pitching stats logic
    });

    const currentTeamData = Object.values(teamStats).find(team => team.isCurrentTeam);
    const opponentTeamData = Object.values(teamStats).find(team => !team.isCurrentTeam);

    if (!currentTeamData) {
      setError('Could not identify current team in game data');
      return;
    }

    let gameInfo = {
      date: currentTeamData.gameDay,
      homeTeam: homeTeam ? homeTeam.name : '',
      homeTeamId: homeTeam ? homeTeam.id : '',
      awayTeam: awayTeam ? awayTeam.name : '',
      awayTeamId: awayTeam ? awayTeam.id : '',
      homeScore: homeTeam && teamStats[homeTeam.id] && teamStats[homeTeam.id].battingStats ? teamStats[homeTeam.id].battingStats.BRUNS || 0 : 0,
      awayScore: awayTeam && teamStats[awayTeam.id] && teamStats[awayTeam.id].battingStats ? teamStats[awayTeam.id].battingStats.BRUNS || 0 : 0,
      isHome: currentTeamData.location === 'Home',
      isWin: currentTeamData.isWin,
      isNeutral: currentTeamData.location === 'Neutral' // Add this flag
    };


    const filterNulls = (obj) => Object.fromEntries(
      Object.entries(obj).filter(([_, value]) => value != null)
    );

    const currentStats = {
      ...filterNulls(currentTeamData.battingStats),
      ...filterNulls(currentTeamData.pitchingStats),
      Team: currentTeamData.name,
      Opponent: currentTeamData.opponent,
      Location: currentTeamData.location === 'Home' ? 'H' : 'V',
      WinOrLoss: currentTeamData.isWin ? 'W' : 'L'
    };

    const opponentStats = opponentTeamData ? {
      ...filterNulls(opponentTeamData.battingStats),
      ...filterNulls(opponentTeamData.pitchingStats),
      Team: opponentTeamData.name,
      Opponent: opponentTeamData.opponent,
      Location: opponentTeamData.location === 'Home' ? 'H' : 'V',
      WinOrLoss: opponentTeamData.isWin ? 'W' : 'L'
    } : null;

    if (gameInfo.isNeutral) {
      gameInfo["isWin"] = Number(gameInfo?.homeScore) >= Number(gameInfo?.awayScore)
    }

    setTeamStats({
      currentTeamStats: currentStats,
      opponentTeamStats: opponentStats,
      gameInfo
    });
  };

  const calcFGPercentage = (made, attempted) => {
    return attempted > 0 ? ((made / attempted) * 100).toFixed(1) + '%' : '0.0%';
  };

  const calcThreePointPercentage = (made, attempted) => {
    return attempted > 0 ? ((made / attempted) * 100).toFixed(1) + '%' : '0.0%';
  };

  const calcFTPercentage = (made, attempted) => {
    return attempted > 0 ? ((made / attempted) * 100).toFixed(1) + '%' : '0.0%';
  };

  const calcBattingAverage = (hits, atBats) => {
    return atBats > 0 ? (hits / atBats).toFixed(3).replace(/^0+/, '') : '.000';
  };

  const groupPlayByPeriod = (plays) => {
    if (!plays) return {};

    return plays.reduce((groups, play) => {
      const period = play.Period;
      if (!groups[period]) {
        groups[period] = [];
      }
      groups[period].push(play);
      return groups;
    }, {});
  };

  const formatTime = (time) => {
    if (!time || time === "NaN") return '';
    return time;
  };

  const isScoringPlay = (play) => {
    return play.ScoringPlay === true || play.Points > 0;
  };

  const getPlayIcon = (tags, description) => {
    if (!tags) return null;

    const tagStr = tags.toUpperCase();

    if (tagStr.includes('SHOT') || tagStr.includes('FG')) {
      return (
        <svg className="h-4 w-4 text-orange-600" fill="currentColor" viewBox="0 0 20 20">
          <circle cx="10" cy="10" r="8" />
        </svg>
      );
    } else if (tagStr.includes('FT') || tagStr.includes('FREE')) {
      return (
        <svg className="h-4 w-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      );
    } else if (tagStr.includes('REBOUND')) {
      return (
        <svg className="h-4 w-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
      );
    } else if (tagStr.includes('ASSIST')) {
      return (
        <svg className="h-4 w-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
        </svg>
      );
    } else if (tagStr.includes('FOUL')) {
      return (
        <svg className="h-4 w-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
        </svg>
      );
    } else if (tagStr.includes('TO') || tagStr.includes('TURNOVER')) {
      return (
        <svg className="h-4 w-4 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
    }

    return null;
  };

  useEffect(() => {
    const toggleSidebar = () => {
      if (isOpen) {
        const sidebarElement = document.querySelector('[data-sidebar]');
        const appContent = document.querySelector('[data-app-content]');

        if (sidebarElement) {
          sidebarElement.classList.add('sidebar-collapsed');
        }

        if (appContent) {
          appContent.classList.add('content-with-slider');
        }
      } else {
        const appContent = document.querySelector('[data-app-content]');
        if (appContent) {
          appContent.classList.remove('content-with-slider');
        }
      }
    };

    toggleSidebar();

    return () => {
      const appContent = document.querySelector('[data-app-content]');
      if (appContent) {
        appContent.classList.remove('content-with-slider');
      }
    };
  }, [isOpen]);

  const sliderClasses = `fixed top-0 bottom-0 right-0 bg-white shadow-2xl w-3/4 md:w-2/3 lg:w-1/2 xl:w-2/5 transition-transform duration-300 ease-in-out transform z-50 overflow-y-auto ${isOpen ? 'translate-x-0' : 'translate-x-full'
    }`;

  if (!isOpen) return null;

  const navigateToGame = (direction) => {
    if (!gameStats || !gameStats.items || !gameStats.items.length) return;

    const gameLinks = document.querySelectorAll('tr[data-game-id]');
    const gameIds = Array.from(gameLinks).map(link => link.getAttribute('data-game-id'));

    if (!gameIds.length) return;

    const currentIndex = gameIds.findIndex(id => id === String(gameId));
    if (currentIndex === -1) return;

    let nextIndex;
    if (direction === 'next') {
      nextIndex = (currentIndex + 1) % gameIds.length;
    } else {
      nextIndex = (currentIndex - 1 + gameIds.length) % gameIds.length;
    }

    const nextGameId = gameIds[nextIndex];
    if (nextGameId && nextGameId !== String(gameId)) {
      setLoading(true);
      setError(null);
      setPlayByPlay(null);
      setActiveTab('stats');

      const navigateEvent = new CustomEvent('gameNavigation', {
        detail: { gameId: nextGameId }
      });
      window.dispatchEvent(navigateEvent);
    }
  };

  const handleTabClick = (tab) => {
    setActiveTab(tab);
    if (tab === 'playbyplay' && !playByPlay) {
      fetchPlayByPlay();
    }
  };

  // const renderPlayByPlay = () => {
  //   if (playByPlayLoading) {
  //     return (
  //       <div className="flex flex-col items-center justify-center h-64">
  //         <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500 mb-4"></div>
  //         <p className="text-gray-600">Loading play-by-play data...</p>
  //       </div>
  //     );
  //   }

  //   if (playByPlayError) {
  //     return (
  //       <div className="flex flex-col items-center justify-center h-64">
  //         <div className="text-red-500 mb-4">
  //           <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
  //             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  //           </svg>
  //         </div>
  //         <p className="text-gray-600 text-center">{playByPlayError}</p>
  //       </div>
  //     );
  //   }

  //   if (!playByPlay || playByPlay.length === 0) {
  //     return (
  //       <div className="flex flex-col items-center justify-center h-64">
  //         <p className="text-gray-600">No play-by-play data available for this game.</p>
  //       </div>
  //     );
  //   }

  //   const groupedPlays = groupPlayByPeriod(playByPlay);
  //   const periods = Object.keys(groupedPlays).sort((a, b) => parseInt(a) - parseInt(b));

  //   return (
  //     <div className="space-y-6">
  //       {periods.map(period => (
  //         <div key={period} className="bg-white rounded-lg shadow-sm border">
  //           <div className="bg-gray-50 px-4 py-3 border-b">
  //             <h3 className="text-lg font-semibold text-gray-800">
  //               {sportType === 'WBB' ? `Period ${period}` : `Inning ${period}`}
  //             </h3>
  //           </div>
  //           <div className="divide-y divide-gray-100">
  //             {groupedPlays[period].map((play, index) => (
  //               <div 
  //                 key={play.PlaySequence || index}
  //                 className={`p-4 hover:bg-gray-50 transition-colors ${
  //                   isScoringPlay(play) ? 'bg-yellow-50 border-l-4 border-l-yellow-400' : ''
  //                 }`}
  //               >
  //                 <div className="flex items-start justify-between">
  //                   <div className="flex items-start space-x-3 flex-1">
  //                     <div className="flex-shrink-0 mt-1">
  //                       {getPlayIcon(play.Tags, play.Description)}
  //                     </div>
  //                     <div className="flex-1 min-w-0">
  //                       <div className="flex items-center space-x-2 mb-1">
  //                         <span className="text-sm font-medium text-gray-900">
  //                           {formatTime(play.Time)}
  //                         </span>
  //                         {play.Team && (
  //                           <span className={`text-xs px-2 py-1 rounded-full ${
  //                             String(play.TeamID) === String(teamId) 
  //                               ? 'bg-blue-100 text-blue-800' 
  //                               : 'bg-gray-100 text-gray-800'
  //                           }`}>
  //                             {play.TeamAbbrev || play.Team}
  //                           </span>
  //                         )}
  //                         {isScoringPlay(play) && (
  //                           <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-800">
  //                             +{play.Points} pts
  //                           </span>
  //                         )}
  //                       </div>
  //                       <p className="text-sm text-gray-700">{play.Description}</p>
  //                     </div>
  //                   </div>
  //                   <div className="flex-shrink-0 ml-4 text-right">
  //                     <div className="text-sm font-medium text-gray-900">
  //                       {play.ScoreVis} - {play.ScoreHome}
  //                     </div>
  //                     <div className="text-xs text-gray-500">
  //                       {play.Visitor} vs {play.Home}
  //                     </div>
  //                   </div>
  //                 </div>
  //               </div>
  //             ))}
  //           </div>
  //         </div>
  //       ))}
  //     </div>
  //   );
  // };

  const renderBasketballStats = () => {
    return (
      <>
        {/* Game Header */}
        <div className="bg-gradient-to-r from-blue-800 to-blue-600 text-white rounded-xl p-6 mb-6">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-xl font-bold">Game Statistics</h2>
              <p className="text-blue-100">
                {new Date(teamStats.gameInfo.date).toLocaleDateString(undefined, {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </p>
            </div>
            <div className="bg-white/20 backdrop-blur-sm rounded-lg px-3 py-1">
              <span className={teamStats.gameInfo.isWin ? 'text-green-300' : 'text-red-300'}>
                {teamStats.gameInfo.isWin ? 'WIN' : 'LOSS'}
              </span>
            </div>
          </div>

          <div className="flex items-center justify-center mt-6 bg-blue-700/30 rounded-lg p-4">
            <div className="text-center flex-1">
              <p className="font-bold">{teamStats.gameInfo.awayTeam}</p>
              <p className="text-3xl font-bold my-2">{teamStats.gameInfo.awayScore}</p>
              <p className="text-xs text-blue-200">
                {teamStats.gameInfo.isNeutral ? 'TEAM 1' : 'AWAY'}
              </p>
            </div>

            <div className="mx-4 text-xl font-bold">
              {teamStats.gameInfo.isNeutral ? '@' : 'VS'}
            </div>

            <div className="text-center flex-1">
              <p className="font-bold">{teamStats.gameInfo.homeTeam}</p>
              <p className="text-3xl font-bold my-2">{teamStats.gameInfo.homeScore}</p>
              <p className="text-xs text-blue-200">
                {teamStats.gameInfo.isNeutral ? 'TEAM 2' : 'HOME'}
              </p>
            </div>
          </div>
        </div>

        {/* Team Stats Table */}
        <div className="mb-8">
          <h3 className="text-xl font-bold border-b pb-2 mb-4 flex items-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-blue-600" viewBox="0 0 20 20" fill="currentColor">
              <path d="M18 3a1 1 0 00-1.196-.98l-10 2A1 1 0 006 5v.8a1 1 0 00.7.95l9 2a1 1 0 001.3-.95V4a1 1 0 00-.8-.98l.5.08zM6 7v2.5a1 1 0 001.5.87l9-2V5l-9 2a1 1 0 00-1.5.87V7z" />
            </svg>
            Team Statistics
          </h3>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50 rounded-tl-lg">Team</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">PTS</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">FG</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">3PT</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">FT</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">REB</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">AST</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">STL</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">BLK</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50 rounded-tr-lg">TO</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {teamStats.currentTeamStats && (
                  <tr className="bg-blue-50 font-medium">
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium">
                      {teamStats.currentTeamStats.Team}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-center">{teamStats.currentTeamStats.PTS || 0}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-center">
                      {teamStats.currentTeamStats.FGM || 0}/{teamStats.currentTeamStats.FGA || 0}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-center">
                      {teamStats.currentTeamStats.ThreeFM || 0}/{teamStats.currentTeamStats.ThreeFA || 0}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-center">
                      {teamStats.currentTeamStats.FTM || 0}/{teamStats.currentTeamStats.FTA || 0}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-center">{teamStats.currentTeamStats.REB || 0}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-center">{teamStats.currentTeamStats.AST || 0}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-center">{teamStats.currentTeamStats.STL || 0}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-center">{teamStats.currentTeamStats.BLK || 0}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-center">{teamStats.currentTeamStats.TO || 0}</td>
                  </tr>
                )}
                {teamStats.opponentTeamStats && (
                  <tr>
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium">
                      {teamStats.opponentTeamStats.Team}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-center">{teamStats.opponentTeamStats.PTS || 0}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-center">
                      {teamStats.opponentTeamStats.FGM || 0}/{teamStats.opponentTeamStats.FGA || 0}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-center">
                      {teamStats.opponentTeamStats.ThreeFM || 0}/{teamStats.opponentTeamStats.ThreeFA || 0}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-center">
                      {teamStats.opponentTeamStats.FTM || 0}/{teamStats.opponentTeamStats.FTA || 0}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-center">{teamStats.opponentTeamStats.REB || 0}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-center">{teamStats.opponentTeamStats.AST || 0}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-center">{teamStats.opponentTeamStats.STL || 0}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-center">{teamStats.opponentTeamStats.BLK || 0}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-center">{teamStats.opponentTeamStats.TO || 0}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Shooting Percentages */}
        <div className="mb-8">
          <h3 className="text-xl font-bold border-b pb-2 mb-4 flex items-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-blue-600" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd" />
            </svg>
            Shooting Percentages
          </h3>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50 rounded-tl-lg">Team</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">FG%</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">3PT%</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50 rounded-tr-lg">FT%</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {teamStats.currentTeamStats && (
                  <tr className="bg-blue-50 font-medium">
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium">
                      {teamStats.currentTeamStats.Team}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-center">
                      {calcFGPercentage(teamStats.currentTeamStats.FGM || 0, teamStats.currentTeamStats.FGA || 0)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-center">
                      {calcThreePointPercentage(teamStats.currentTeamStats.ThreeFM || 0, teamStats.currentTeamStats.ThreeFA || 0)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-center">
                      {calcFTPercentage(teamStats.currentTeamStats.FTM || 0, teamStats.currentTeamStats.FTA || 0)}
                    </td>
                  </tr>
                )}
                {teamStats.opponentTeamStats && (
                  <tr>
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium">
                      {teamStats.opponentTeamStats.Team}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-center">
                      {calcFGPercentage(teamStats.opponentTeamStats.FGM || 0, teamStats.opponentTeamStats.FGA || 0)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-center">
                      {calcThreePointPercentage(teamStats.opponentTeamStats.ThreeFM || 0, teamStats.opponentTeamStats.ThreeFA || 0)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-center">
                      {calcFTPercentage(teamStats.opponentTeamStats.FTM || 0, teamStats.opponentTeamStats.FTA || 0)}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Visual Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Team Performance Card */}
          <div className="bg-white rounded-xl shadow-md p-6">
            <h4 className="text-lg font-bold mb-4 text-gray-800 flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 8v8m-4-5v5m-4-2v2m-2 4h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              {teamStats.currentTeamStats?.Team} Performance
            </h4>

            {teamStats.currentTeamStats && (
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between mb-1 text-sm">
                    <span className="text-gray-600">Field Goal %</span>
                    <span className="font-medium">
                      {teamStats.currentTeamStats.FGA > 0
                        ? ((teamStats.currentTeamStats.FGM / teamStats.currentTeamStats.FGA) * 100).toFixed(1)
                        : '0.0'}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="themePrimary h-2 rounded-full"
                      style={{ width: `${teamStats.currentTeamStats.FGA > 0 ? (teamStats.currentTeamStats.FGM / teamStats.currentTeamStats.FGA) * 100 : 0}%` }}
                    ></div>
                  </div>
                </div>

                <div>
                  <div className="flex justify-between mb-1 text-sm">
                    <span className="text-gray-600">Three Point %</span>
                    <span className="font-medium">
                      {teamStats.currentTeamStats.ThreeFA > 0
                        ? ((teamStats.currentTeamStats.ThreeFM / teamStats.currentTeamStats.ThreeFA) * 100).toFixed(1)
                        : '0.0'}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="themeSecondary h-2 rounded-full"
                      style={{ width: `${teamStats.currentTeamStats.ThreeFA > 0 ? (teamStats.currentTeamStats.ThreeFM / teamStats.currentTeamStats.ThreeFA) * 100 : 0}%` }}
                    ></div>
                  </div>
                </div>

                <div>
                  <div className="flex justify-between mb-1 text-sm">
                    <span className="text-gray-600">Free Throw %</span>
                    <span className="font-medium">
                      {teamStats.currentTeamStats.FTA > 0
                        ? ((teamStats.currentTeamStats.FTM / teamStats.currentTeamStats.FTA) * 100).toFixed(1)
                        : '0.0'}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="themeSecondary h-2 rounded-full"
                      style={{ width: `${teamStats.currentTeamStats.FTA > 0 ? (teamStats.currentTeamStats.FTM / teamStats.currentTeamStats.FTA) * 100 : 0}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Team Comparison Card */}
          <div className="bg-white rounded-xl shadow-md p-6">
            <h4 className="text-lg font-bold mb-4 text-gray-800 flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
              </svg>
              Team Comparison
            </h4>

            {teamStats.currentTeamStats && teamStats.opponentTeamStats && (
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between mb-1 text-sm">
                    <span className="text-gray-600">Points</span>
                    <span className="font-medium">
                      {teamStats.currentTeamStats.PTS} vs {teamStats.opponentTeamStats.PTS}
                    </span>
                  </div>
                  <div className="flex h-2 rounded-full overflow-hidden">
                    <div
                      className="themeSecondary"
                      style={{
                        width: `${(teamStats.currentTeamStats.PTS / (teamStats.currentTeamStats.PTS + teamStats.opponentTeamStats.PTS)) * 100}%`
                      }}
                    ></div>
                    <div
                      className="bg-red-600"
                      style={{
                        width: `${(teamStats.opponentTeamStats.PTS / (teamStats.currentTeamStats.PTS + teamStats.opponentTeamStats.PTS)) * 100}%`
                      }}
                    ></div>
                  </div>
                </div>

                <div>
                  <div className="flex justify-between mb-1 text-sm">
                    <span className="text-gray-600">Rebounds</span>
                    <span className="font-medium">
                      {teamStats.currentTeamStats.REB} vs {teamStats.opponentTeamStats.REB}
                    </span>
                  </div>
                  <div className="flex h-2 rounded-full overflow-hidden">
                    <div
                      className="themeSecondary"
                      style={{
                        width: `${(teamStats.currentTeamStats.REB / (teamStats.currentTeamStats.REB + teamStats.opponentTeamStats.REB)) * 100}%`
                      }}
                    ></div>
                    <div
                      className="bg-red-600"
                      style={{
                        width: `${(teamStats.opponentTeamStats.REB / (teamStats.currentTeamStats.REB + teamStats.opponentTeamStats.REB)) * 100}%`
                      }}
                    ></div>
                  </div>
                </div>

                <div>
                  <div className="flex justify-between mb-1 text-sm">
                    <span className="text-gray-600">Assists</span>
                    <span className="font-medium">
                      {teamStats.currentTeamStats.AST} vs {teamStats.opponentTeamStats.AST}
                    </span>
                  </div>
                  <div className="flex h-2 rounded-full overflow-hidden">
                    <div
                      className="themeSecondary"
                      style={{
                        width: `${(teamStats.currentTeamStats.AST / (teamStats.currentTeamStats.AST + teamStats.opponentTeamStats.AST)) * 100}%`
                      }}
                    ></div>
                    <div
                      className="bg-red-600"
                      style={{
                        width: `${(teamStats.opponentTeamStats.AST / (teamStats.currentTeamStats.AST + teamStats.opponentTeamStats.AST)) * 100}%`
                      }}
                    ></div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </>
    );
  };

  const renderBaseballStats = () => {
    return (
      <>
        {/* Game Header */}
        <div className="bg-gradient-to-r from-green-800 to-green-600 text-white rounded-xl p-6 mb-6">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-xl font-bold">Game Statistics</h2>
              <p className="text-green-100">
                {new Date(teamStats.gameInfo.date).toLocaleDateString(undefined, {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </p>
            </div>
            <div className="bg-white/20 backdrop-blur-sm rounded-lg px-3 py-1">
              <span className={teamStats.gameInfo.isWin ? 'text-green-300' : 'text-red-300'}>
                {teamStats.gameInfo.isWin ? 'WIN' : 'LOSS'}
              </span>
            </div>
          </div>

          <div className="flex items-center justify-center mt-6 bg-green-700/30 rounded-lg p-4">
            <div className="text-center flex-1">
              <p className="font-bold">{teamStats.gameInfo.awayTeam}</p>
              <p className="text-3xl font-bold my-2">{teamStats.gameInfo.awayScore}</p>
              <p className="text-xs text-blue-200">
                {teamStats.gameInfo.isNeutral ? 'TEAM 1' : 'AWAY'}
              </p>
            </div>

            <div className="mx-4 text-xl font-bold">
              {teamStats.gameInfo.isNeutral ? '@' : 'VS'}
            </div>

            <div className="text-center flex-1">
              <p className="font-bold">{teamStats.gameInfo.homeTeam}</p>
              <p className="text-3xl font-bold my-2">{teamStats.gameInfo.homeScore}</p>
              <p className="text-xs text-blue-200">
                {teamStats.gameInfo.isNeutral ? 'TEAM 2' : 'HOME'}
              </p>
            </div>
          </div>
        </div>

        {/* Batting Stats */}
        <div className="mb-8">
          <h3 className="text-xl font-bold border-b pb-2 mb-4 flex items-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-green-600" viewBox="0 0 20 20" fill="currentColor">
              <path d="M18 3a1 1 0 00-1.196-.98l-10 2A1 1 0 006 5v.8a1 1 0 00.7.95l9 2a1 1 0 001.3-.95V4a1 1 0 00-.8-.98l.5.08zM6 7v2.5a1 1 0 001.5.87l9-2V5l-9 2a1 1 0 00-1.5.87V7z" />
            </svg>
            Batting Statistics
          </h3>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50 rounded-tl-lg">Team</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">R</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">H</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">RBI</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">2B</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">3B</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">HR</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">BB</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">SO</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50 rounded-tr-lg">AVG</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {teamStats.currentTeamStats && (
                  <tr className="bg-green-50 font-medium">
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium">
                      {teamStats.currentTeamStats.Team}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-center">{teamStats.currentTeamStats.BRUNS || 0}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-center">{teamStats.currentTeamStats.BHITS || 0}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-center">{teamStats.currentTeamStats.BRBI || 0}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-center">{teamStats.currentTeamStats.B2B || 0}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-center">{teamStats.currentTeamStats.B3B || 0}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-center">{teamStats.currentTeamStats.BHR || 0}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-center">{teamStats.currentTeamStats.BBB || 0}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-center">{teamStats.currentTeamStats.BSO || 0}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-center">
                      {calcBattingAverage(teamStats.currentTeamStats.BHITS || 0, teamStats.currentTeamStats.BAB || 0)}
                    </td>
                  </tr>
                )}
                {teamStats.opponentTeamStats && (
                  <tr>
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium">
                      {teamStats.opponentTeamStats.Team}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-center">{teamStats.opponentTeamStats.BRUNS || 0}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-center">{teamStats.opponentTeamStats.BHITS || 0}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-center">{teamStats.opponentTeamStats.BRBI || 0}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-center">{teamStats.opponentTeamStats.B2B || 0}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-center">{teamStats.opponentTeamStats.B3B || 0}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-center">{teamStats.opponentTeamStats.BHR || 0}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-center">{teamStats.opponentTeamStats.BBB || 0}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-center">{teamStats.opponentTeamStats.BSO || 0}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-center">
                      {calcBattingAverage(teamStats.opponentTeamStats.BHITS || 0, teamStats.opponentTeamStats.BAB || 0)}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pitching Stats */}
        <div className="mb-8">
          <h3 className="text-xl font-bold border-b pb-2 mb-4 flex items-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-blue-600" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd" />
            </svg>
            Pitching Statistics
          </h3>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50 rounded-tl-lg">Team</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">IP</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">H</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">R</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">ER</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">BB</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">SO</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">HR</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50 rounded-tr-lg">ERA</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {teamStats.currentTeamStats && (
                  <tr className="bg-blue-50 font-medium">
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium">
                      {teamStats.currentTeamStats.Team}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-center">{teamStats.currentTeamStats.PIP || 0}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-center">{teamStats.currentTeamStats.PHITS || 0}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-center">{teamStats.currentTeamStats.PRUNS || 0}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-center">{teamStats.currentTeamStats.PER || 0}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-center">{teamStats.currentTeamStats.PBB || 0}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-center">{teamStats.currentTeamStats.PSO || 0}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-center">{teamStats.currentTeamStats.PHR || 0}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-center">
                      {teamStats.currentTeamStats.PIP > 0 ?
                        ((teamStats.currentTeamStats.PER * 9) / teamStats.currentTeamStats.PIP).toFixed(2) :
                        '0.00'}
                    </td>
                  </tr>
                )}
                {teamStats.opponentTeamStats && (
                  <tr>
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium">
                      {teamStats.opponentTeamStats.Team}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-center">{teamStats.opponentTeamStats.PIP || 0}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-center">{teamStats.opponentTeamStats.PHITS || 0}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-center">{teamStats.opponentTeamStats.PRUNS || 0}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-center">{teamStats.opponentTeamStats.PER || 0}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-center">{teamStats.opponentTeamStats.PBB || 0}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-center">{teamStats.opponentTeamStats.PSO || 0}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-center">{teamStats.opponentTeamStats.PHR || 0}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-center">
                      {teamStats.opponentTeamStats.PIP > 0 ?
                        ((teamStats.opponentTeamStats.PER * 9) / teamStats.opponentTeamStats.PIP).toFixed(2) :
                        '0.00'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </>
    );
  };

  return (
    <div className="fixed inset-0 z-40 flex">
      <div
        className="fixed inset-0 bg-black bg-opacity-25 transition-opacity"
        onClick={onClose}
      ></div>

      <div className={sliderClasses}>
        {/* Header with Navigation */}
        <div className="flex justify-between items-center absolute top-0 left-0 right-0 p-4 bg-white border-b z-10">
          <div className="flex">
            <button
              onClick={() => navigateToGame('prev')}
              className="p-2 rounded-full hover:bg-gray-100 text-gray-700 focus:outline-none"
              title="Previous Game"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              onClick={() => navigateToGame('next')}
              className="p-2 rounded-full hover:bg-gray-100 text-gray-700 focus:outline-none ml-1"
              title="Next Game"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-100 text-gray-700 focus:outline-none"
            title="Close"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="absolute top-16 left-0 right-0 bg-white border-b z-10">
          <div className="flex">
            <button
              onClick={() => handleTabClick('stats')}
              className={`flex-1 px-4 py-3 text-sm font-medium focus:outline-none transition-colors ${activeTab === 'stats'
                ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
            >
              <div className="flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                Statistics
              </div>
            </button>
            <button
              onClick={() => handleTabClick('playbyplay')}
              className={`flex-1 px-4 py-3 text-sm font-medium focus:outline-none transition-colors ${activeTab === 'playbyplay'
                ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
            >
              <div className="flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
                Play-by-Play
              </div>
            </button>
          </div>
        </div>

        {/* Content Area */}
        <div className="p-6 h-full pt-32">
          {loading ? (
            <div className="flex flex-col items-center justify-center h-full">
              <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-blue-500 mb-4"></div>
              <p className="text-gray-600">Loading game statistics...</p>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center h-full">
              <div className="text-red-500 mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p className="text-gray-600">{error}</p>
            </div>
          ) : (
            <>
              {/* Render content based on active tab */}


              <PlayByPlay
                playByPlay={playByPlay}
                isVisible={activeTab === 'playbyplay'} // or whatever your play-by-play tab value is
                sportType={sportType}
              />

              {/* Always render stats but control visibility */}
              <div className={activeTab === 'stats' ? '' : 'hidden'}>
                {(sportType === 'WBB' || sportType === 'MBB') ? renderBasketballStats() : renderBaseballStats()}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default GameStatSlider;